import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCoalesced } from "@/hooks/use-coalesced";
import { useMemberStream } from "@/hooks/use-member-stream";
import { Link, useParams } from "react-router-dom";
import { Eye, EyeOff, Pencil, Printer, RefreshCw, ShoppingCart, X } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";
import { toast } from "sonner";

import businessBanner from "@/assets/business-member-banner.png";
import goldBanner from "@/assets/gold-member-banner.png";
import logo from "@/assets/logo-light.png";
import { AvatarPicker } from "@/components/avatar-picker";
import {
  type Amendment,
  AmendmentDialog,
  PrintDialog,
  useAllowedAmendments,
} from "@/components/member-actions";
import { type HistoryKey, MemberHistories } from "@/components/member-histories";
import { AddAttachmentModal, ViewAttachmentsModal } from "@/components/member-attachments";
import { AcquisitionDialog } from "@/components/member-acquisition";
import { CardInfoDialog } from "@/components/member-card-info";
import { ConsentDialog } from "@/components/member-consent";
import { type Note, NoteFormModal, ViewNotesModal } from "@/components/member-notes";
import { TAKES_SUPPLEMENTARY, PrimaryMoveDialog } from "@/components/member-primary-move";
import { EditDialog, type FieldDef } from "@/components/resource-page";
import { Badge } from "@/components/ui/badge";
import { Button, IconButton } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Checkbox, Form, Input } from "@/components/ui/field";
import { OptionSelect } from "@/components/ui/option-select";
import { BackArrow, backLinkClass } from "@/components/ui/page";
import { Skeleton, SkeletonLines } from "@/components/ui/skeleton";
import { Tooltip } from "@/components/ui/tooltip";
import { api, errorMessage, ifMatch } from "@/services/api";
import type { TranslationKey } from "@/constants/i18n";
import { useCan, useRank } from "@/utils/permissions";
import { cn } from "@/utils/utils";
import {
  ACCOUNT_TYPE,
  ACQUISITION,
  channelsFor,
  GENDER,
  MEMBERSHIP_TYPE,
  type Member,
  PREFIX,
  STATUS,
  SUFFIX,
  TIER,
  TIERS,
  cardNameOf,
  day,
  fields,
  fullName,
  label,
  nameWithSuffix,
  toForm,
} from "@/pages/MembersPage";
import { useTranslation } from "@/store/locale";

const MASK = "********";

/**
 * The statuses an Associate may read but not edit.
 *
 * A card that has been blacklisted, cancelled or allowed to lapse is out of
 * service, and correcting what it holds is a rank above — see `frozen` below.
 */
const BEYOND_AN_ASSOCIATE = new Set(["blacklisted", "cancelled", "expired"]);

/** The full-bleed banner each membership is printed on; the others go bare. */
const BANNER_ART: Record<string, string> = {
  gold: goldBanner,
  business: businessBanner,
};

/** How each account status reads at a glance, as the legacy badge coloured them. */
const STATUS_TONE: Record<string, "success" | "danger" | "warning" | "neutral"> = {
  active: "success",
  expired: "danger",
  blacklisted: "warning",
  cancelled: "neutral",
};

/**
 * The cards a member is edited by, one dialog each — the legacy detail page
 * had a modal per card, and a forty-field form is nobody's idea of an edit.
 * Each names the fields on its card and the route that takes exactly those.
 *
 * Three are not among them. Card Information's three fields answer one question
 * between them — a day, or never — which a list of fields cannot say;
 * Acquisition Type is a single dropdown that a form's full width leaves
 * stranded; and Members Consent is three flags each of which needs the address
 * above it, which is a rule and not a field. `CardInfoDialog`,
 * `AcquisitionDialog` and `ConsentDialog` write those three out.
 */
type Card = "basic" | "business";
const CARDS: Record<Card, { action: string; title: TranslationKey; names: string[] }> = {
  basic: {
    action: "basic-info",
    title: "md.basicInformation",
    names: [
      "prefix",
      "suffix",
      "first_name",
      "middle_name",
      "last_name",
      "gender",
      "date_of_birth",
      "mobile_number",
      "email",
      "unit_number",
      "building_name",
      "address_street",
      "city_id",
      "barangay_id",
      "employee_number",
      "company_name",
    ],
  },
  business: {
    action: "business-info",
    title: "md.businessInformation",
    names: ["business_name", "business_company_name", "business_type", "position"],
  },
};

/**
 * Basic Information as an Associate gets it: the contact details and the
 * address, and the member's name over them to check it against.
 *
 * Legacy's `CLERK_EDITABLE_BASIC_INFO_KEYS`, field for field. What is gone is
 * what a club counter has no business correcting from the desk — the prefix,
 * the suffix, the gender, the birthday, and the employee number or company name
 * the tier was signed up under. What is left is what changes about a person
 * while they are still the same person: they moved, they got a new phone.
 *
 * The three name fields stay on the form and are `showOnly` — legacy drew them
 * disabled and then left them out of the payload, which is the same rule said
 * twice, and the reason for the second half is in `showOnly` itself. A name
 * that is genuinely wrong is a rank above, and on record: it reprints the card.
 */
const ASSOCIATE_BASIC: readonly string[] = [
  "first_name",
  "middle_name",
  "last_name",
  "mobile_number",
  "email",
  "unit_number",
  "building_name",
  "address_street",
  "city_id",
  "barangay_id",
];

/** The three of those an Associate may read but not write. */
const ASSOCIATE_READS_ONLY = new Set(["first_name", "middle_name", "last_name"]);

/**
 * How long one write's refreshes are collapsed into a single round.
 *
 * Long enough to cover the round trip of the server's own `member_changed`
 * frame — it lands about 60ms after the write answers — and short enough that
 * a change somebody else makes still shows up at once.
 */
const SETTLE_MS = 400;

function MemberDetailPage() {
  const { memberId } = useParams() as { memberId: string };
  return <MemberDetail id={Number(memberId)} />;
}

/**
 * The Detail View.
 *
 * `embedded` is for the members list, which takes the page over and draws the
 * standard header itself — the way back, the id, Refresh, Delete and Edit. The
 * body then brings no chrome of its own, or the page carries two of everything.
 * Standalone at `/members/$memberId`, it brings its own breadcrumb.
 */
export function MemberDetail({
  id,
  onClose,
  embedded = false,
}: {
  id: number;
  onClose?: () => void;
  embedded?: boolean;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const member = useQuery({
    queryKey: ["/members", id],
    queryFn: async ({ signal }) => (await api.get<Member>(`/members/${id}`, { signal })).data,
  });
  /**
   * The print jobs, for the one thing the page needs of them: whether a card is
   * waiting to come out.
   *
   * A print is the tail of something — the enrolment, an amendment — and each
   * of those queues a job. Once that job is printed there is nothing to print
   * until the next amendment queues another, so the button goes quiet rather
   * than offering a second copy of a card already in the member's hand. Same
   * key the print dialog and the history tab ask under, so the three share one
   * answer and `refresh()` moves all of them at once.
   */
  const allow = useCan();
  const printJobs = useQuery({
    queryKey: [`/members/${id}/print-history`],
    queryFn: async ({ signal }) =>
      (await api.get<{ print_status: string }[]>(`/members/${id}/print-history`, { signal }))
        .data,
    // Not asked for at all without the right to read it. The route answers
    // 403, which is the correct answer and a useless one: the page opened
    // anyway, the console filled with a refusal nobody caused, and the only
    // thing the answer was for is whether a job is already queued.
    //
    // Left pending when it is not asked, which is what a disabled query is —
    // and that is the right way for this one to fail. The branch below reads
    // "printed, and nothing since has asked for another" from a settled
    // answer, so an unasked one blocks nothing and the button stays live for
    // whoever may print. The server decides, as it would have anyway.
    enabled: allow("view__member_print_history"),
  });
  const [card, setCard] = useState<Card | null>(null);
  const [editingCard, setEditingCard] = useState(false);
  const [editingConsent, setEditingConsent] = useState(false);
  const [editingAcquisition, setEditingAcquisition] = useState(false);
  const [tab, setTab] = useState<HistoryKey>("card");
  const [showBasic, setShowBasic] = useState(false);
  /** The business section's own cover, separate from the link that opens it. */
  const [showBusinessInfo, setShowBusinessInfo] = useState(false);
  const [showBusiness, setShowBusiness] = useState(false);
  const [showCoBrand, setShowCoBrand] = useState(false);
  const [amendment, setAmendment] = useState<Amendment | null>(null);
  const [printing, setPrinting] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [changingAccount, setChangingAccount] = useState(false);
  const [editingFree, setEditingFree] = useState(false);
  /**
   * Which of the four screens beside the member is open — the same four legacy
   * had: view the documents, attach one, read the notes, write one.
   *
   * Editing a note from the notes list keeps the list underneath, so the card
   * is still there when the form closes; `noteForm` holds the note being
   * corrected, or `null` for a new one.
   */
  const [viewing, setViewing] = useState<"attachments" | "notes" | null>(null);
  const [addingAttachment, setAddingAttachment] = useState(false);
  const [noteForm, setNoteForm] = useState<{ note: Note | null } | null>(null);
  const allowed = useAllowedAmendments(id);
  /**
   * Whether the record is one this person may look at but not correct.
   *
   * An Associate edits live accounts. Once a card is out of service —
   * blacklisted, cancelled, or lapsed — what it holds is a record of what was,
   * and putting it right is a rank above: the desk that takes a card out of
   * service is the desk that amends what it said. Higher ranks keep every
   * pencil, on every status.
   *
   * It takes the pencils away and nothing else. An amendment is governed by the
   * matrix the server draws the picker from — which is where a cancelled
   * account's own actions are decided — and a note or an attachment is a record
   * *about* the member rather than a change to it, so neither is touched here.
   */
  const rank = useRank();
  /**
   * A renew window is an expired card let in to shop, not a card put back in
   * service: ``renew`` writes the status active and raises the temporary flag,
   * and the sweep puts the status back to expired once the minutes are up
   * unless the card was renewed for real at the till. So the flag is read here
   * as well as the status — ten minutes at a counter is not the occasion to
   * correct what the record says, and the account it is on is still expired.
   */
  const temporary = !!member.data?.is_temporarily_active;
  const frozen =
    rank === "associate" &&
    (temporary || BEYOND_AN_ASSOCIATE.has(member.data?.account_status ?? ""));
  const canChange = allow("change__member") && !frozen;
  // The one card a Business Primary gives away. Its own right rather than part
  // of `change__member`: giving a card away is a commercial decision, and the
  // desk that fixes a misspelt address is not the desk that makes it.
  const canSetFree = allow("change__member_free_supplementary") && !frozen;
  // Each section of this page has its own route on the server and, since the
  // access document split them, its own codename. `change__member` still covers
  // the record as a whole — the business block, the volume-shopper tag — but the
  // controls below ask for the one that guards the request they send, so none of
  // them is a button whose answer is 403.
  const canEditBasic = allow("change__member_basic_info") && !frozen;
  const canEditCard = allow("change__member_card_info") && !frozen;
  const canEditConsent = allow("change__member_consent") && !frozen;
  const canSetPhoto = allow("change__member_photo") && !frozen;
  const canEditAcquisition = allow("change__member_acquisition_detail") && !frozen;
  const canAmend = allow("change__member_amendment_detail");
  const canPrint = allow("add__member_print_history");
  /**
   * Whether this person may take a supplementary on under *this* primary.
   *
   * Two questions, not one. The first is the act itself. The second is the
   * tier: a supplementary takes its membership from the primary it hangs off,
   * so adding one under a complimentary account is enrolling a complimentary
   * member, and the right to enrol one is `add__member_complimentary` — which
   * neither Supervisor nor Associate holds. Legacy asked the same thing the
   * same way round (`MrmSupplementarySection`: the role's allowed tiers must
   * include the primary's), and showed those two roles no button here.
   *
   * Without the second half the button opened a form that filled in and then
   * refused at the end, which is the worst moment to find out.
   */
  const canAddSupplementary =
    allow("add__member_supplementary_history") &&
    allow(TIERS.find((one) => one.tier === member.data?.membership_tier)?.permission ?? "");

  /**
   * Everything the member is read under, after something has been done to them.
   *
   * `["/members"]` rather than `["/members", id]`: the list asks under
   * `["/members", params]` and its live feed under `["/members", params,
   * "feed"]`, and a key is matched element by element, so neither is a
   * `["/members", 7]` — the row kept the name it was fetched with, and going
   * back from a rename showed the old one until the list was reloaded by hand.
   * The prefix covers the record and every list it appears on at once.
   *
   * The rest by predicate, for the same reason from the other side:
   * `["/members/7/"]` matches nothing at all — the card history asks under
   * `["/members/7/card"]`, one string, not two. An amendment writes a card
   * history line and an amendment line, and neither appeared until the page was
   * reloaded.
   */
  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["/members"] });
    const mine = `/members/${id}/`;
    void queryClient.invalidateQueries({
      predicate: (query) => {
        const first = query.queryKey[0];
        return typeof first === "string" && first.startsWith(mine);
      },
    });
  }, [queryClient, id]);

  /**
   * The same refresh, at most once in a window — see `useCoalesced`. Two paths
   * ask for one write: the mutation that made it, and the `member_changed`
   * frame the server publishes for it. One action can publish several frames of
   * its own, too: a change of tier writes the member, a card-history line, an
   * amendment line and a print job.
   */
  const refresh = useCoalesced(invalidate, SETTLE_MS);
  // The same refresh the button does, run when somebody else is the one who
  // changed this member — another till, an amendment, or the nightly sweep that
  // expires a lapsed card. Without it this page is only ever as current as the
  // moment it was opened.
  useMemberStream(id, refresh);
  // Its own endpoint: an ordinary edit should not carry a few hundred kilobytes
  // of photograph on a path that has nothing to do with one.
  const picture = useMutation({
    // The name travels with the picture: what is stored is a key of our own
    // making, so without it the activity log can only name the photograph by
    // that key. A capture has the name the picker gave it.
    mutationFn: ({ profile_image, file_name }: { profile_image: string; file_name?: string }) =>
      api.put(`/members/${id}/profile-image`, { profile_image, file_name }),
    onSuccess: (_, value) => {
      toast.success(value.profile_image ? t("md.photoUpdated") : t("md.photoRemoved"));
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t("md.photoFailed"))),
  });
  const volume = useMutation({
    mutationFn: (volume_shopper: boolean) =>
      api.patch(`/members/${id}/volume-shopper`, { volume_shopper }, ifMatch(member.data)),
    onSuccess: () => {
      toast.success(t("md.volumeShopperUpdated"));
      setTagging(false);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t("md.updateFailed"))),
  });
  // The free card is a plain edit — which card it is, not what happens to it —
  // and the server refuses a second one under the same primary.
  const freeCard = useMutation({
    mutationFn: (is_free_supplementary: boolean) =>
      api.patch(`/members/${id}`, { is_free_supplementary }, ifMatch(member.data)),
    onSuccess: async () => {
      toast.success(t("md.freeSupplementaryUpdated"));
      setEditingFree(false);
      // The parent gate's answer — whether the primary still has its free card
      // to give — is now wrong for every screen holding it, this member's
      // siblings included.
      await queryClient.invalidateQueries({ queryKey: ["members", "primary"] });
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t("md.updateFailed"))),
  });
  // The page holds its own shape while it loads, so nothing jumps into place.
  if (member.isPending)
    return (
      <div className={cn(embedded ? "" : "px-5 pb-10 md:px-8")} aria-busy>
        <Skeleton className="rounded-card mt-8 h-60" />
        <div className="flex items-center gap-6 px-8">
          <Skeleton className="-mt-[50px] size-40 shrink-0 rounded-full" />
          <div className="grid flex-1 gap-2">
            <Skeleton className="h-7 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="mt-8 grid gap-8 lg:grid-cols-[16rem_minmax(0,1fr)_17rem]">
          <SkeletonLines rows={7} />
          <SkeletonLines rows={10} />
          <SkeletonLines rows={6} />
        </div>
      </div>
    );
  if (!member.data)
    return (
      <EmptyState
        kind="error"
        title={t("md.notFound")}
        description={t("md.notFoundBody")}
        action={
          <Button asChild variant="secondary">
            <Link to="/members">{t("md.backToMembers")}</Link>
          </Button>
        }
      />
    );
  const m = member.data;
  // An active or expired card holds a slot; a cancelled or blacklisted one has
  // given it back. The server counts the same way — see `SLOT_HELD_BY`.
  const slotsFull =
    (m.supplementary_limit ?? 0) > 0 &&
    (m.supplementaries_in_use ?? 0) >= (m.supplementary_limit ?? 0);
  const masked = (value: string | null | undefined) => (showBasic ? (value ?? "—") : MASK);
  // A member's business is their own, as their name and address are: the
  // section opens covered and the eye beside the heading uncovers it, which is
  // the one gesture the page already has for this.
  const businessMasked = (value: string | null | undefined) =>
    showBusinessInfo ? (value ?? "—") : MASK;
  const title = [
    label(MEMBERSHIP_TYPE(t), m.membership_type),
    label(TIER(t), m.membership_tier),
  ]
    .filter((t) => t !== "—")
    .join(" ");
  // The banner says the membership and the account type on one line, as the
  // card does — "Gold Member (Primary)", not the type on a line of its own.
  const accountType = label(ACCOUNT_TYPE(t), m.account_type);
  const bannerTitle = [title, accountType === "—" ? "" : `(${accountType})`]
    .filter(Boolean)
    .join(" ");
  // Gold and Business are printed on their own artwork; the rest are the flat navy.
  const bannerArt = BANNER_ART[m.membership_type ?? ""];
  const status = label(STATUS(t), m.account_status);
  // Why the card cannot be printed right now, if it cannot — the same reasons
  // legacy showed on the disabled button, so the clerk knows what to fix.
  /**
   * The newest job only, which is the one a print takes.
   *
   * Not "any job still pending": every amendment queues one, and a member who
   * has been upgraded, re-carded and renewed carries the stragglers of all
   * three. A print marks the newest of them printed — so with "any" the button
   * stayed lit after printing, offering a second copy of the card that had
   * just come out. The rows arrive newest first.
   */
  // A photograph goes on a live account only, the same rule printing follows
  // below — the picture is for the card, and a cancelled or blacklisted account
  // is not getting one. Permission and status both, so the API refusing is the
  // exception rather than the way somebody finds out.
  //
  // This closes the picker outright, removal included. The API is the more
  // permissive of the two and takes a removal from a closed account — clearing
  // something off one is tidying, not issuing — but `AvatarPicker` has one
  // switch for the whole control, and a picker open only to delete is a worse
  // answer than a closed one.
  // ...except for a superuser, who is not held by the live-account rule — the
  // API lets them past it too. The rule is there to stop a desk issuing against
  // a closed account by habit, not to stop the one account with full access
  // correcting a wrong face on an expired membership.
  const mayTakePhoto = canSetPhoto && (m.account_status === "active" || rank === "super_user");
  // The cart beside the picture sends `PATCH /members/{id}/volume-shopper`,
  // which asks for `change__member` — so that is what lights it, rather than
  // the photograph's `change__member_photo` it had been borrowing from the
  // avatar it sits on.
  //
  // The *other* half of what it borrowed turns out to belong here after all. A
  // volume shopper is somebody buying at the till, and a cancelled account
  // buys nothing: tagging one is tidying a record that has already closed, and
  // a supervisor doing it by habit on the wrong screen is exactly what the
  // live-account rule is for. Lifting that rule to let a superuser past it
  // lifted it for everybody, which was more than it needed to be.
  //
  // So: the same shape as `mayTakePhoto` above. A live account for everyone,
  // and the one rank with full access is not held by it — the API would take
  // the change from them, and telling them they lack permission for something
  // they can do is its own kind of wrong.
  const mayTagVolumeShopper =
    canChange && (m.account_status === "active" || rank === "super_user");
  const awaitingPrint = printJobs.data?.[0]?.print_status === "pending";
  const printBlock = !m.profile_image
    ? t("md.printNeedsPhoto")
    : m.account_status !== "active"
      ? t("md.printNotActive").replace("{status}", status.toLowerCase())
      : m.is_temporarily_active
        ? t("md.printTemporary")
        : // Printed, and nothing since has asked for another. An amendment
          // queues the next job and the button comes back with it.
          !printJobs.isPending && !awaitingPrint
          ? t("md.printAlreadyPrinted")
          : null;

  // The fields of the open card, in the card's order, sans the section
  // headings a one-card form has no use for. The tier decides two of them.
  const cardFields = (which: Card): FieldDef[] => {
    const all = fields(t, rank);
    // An Associate's Basic Information is a shorter card, in the same order.
    // See `ASSOCIATE_BASIC`; every other rank gets the whole of it.
    const names =
      which === "basic" && rank === "associate"
        ? CARDS.basic.names.filter((name) => ASSOCIATE_BASIC.includes(name))
        : CARDS[which].names;
    return names.flatMap((name) => {
      const field = all.find((f) => f.name === name);
      if (!field) return [];
      if (name === "employee_number" && m.membership_tier !== "employee") return [];
      if (name === "company_name" && m.membership_tier !== "affiliates") return [];
      const { section: _section, ...rest } = field;
      if (rank === "associate" && ASSOCIATE_READS_ONLY.has(name))
        return [{ ...rest, showOnly: true }];
      // A member is enrolled over the counter or by a canvasser; "benefits" is
      // the other three tiers' channel and not a choice here, as legacy had it,
      // and "online" is only ever the channel an enrolment was filed under.
      if (name === "acquisition_type" && m.membership_tier === "member")
        return [
          {
            ...rest,
            // The rank's own list, less "online": that is the channel an
            // enrolment was filed under, not one a club counter sets. The
            // account's own channel survives both cuts — a field that cannot
            // say what happened is a field whose every answer is wrong.
            options: channelsFor(t, rank, m.acquisition_type).filter(
              (o) => o.value !== "online" || o.value === m.acquisition_type,
            ),
          },
        ];
      return [rest];
    });
  };

  return (
    // `Page` already ends with its own padding, so embedded adds none.
    <div className={embedded ? "" : "px-5 pb-10 md:px-8"}>
      {/* Legacy's own heading, not a trail of links: what a record page is
          asked at the top is "which member am I looking at", and "MRM system ›
          Members › Detail View" answered it with the two things the person
          already knew and one that names the screen rather than the member.
          The way back is the chevron, where legacy put it. */}
      <nav
        className={cn("flex items-center gap-3 py-4", embedded && "hidden")}
        aria-label={t("page.memberDetail")}
      >
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="text-fg-subtle hover:bg-surface-inset hover:text-fg ml-auto grid size-9 place-items-center rounded-lg transition-colors"
          >
            <X className="size-5" aria-hidden />
            <span className="sr-only">{t("common.close")}</span>
          </button>
        ) : (
          <>
            {/* The same back control every other header uses, rather than a
                hand-rolled copy of the one it used to be — this page is where
                the bare chevron looked worst, beside a heading several times
                its weight. */}
            <Link to="/members" className={backLinkClass}>
              <BackArrow label={t("rp.back")} />
            </Link>
            <div className="min-w-0">
              <p className="text-eyebrow text-fg-subtle">{t("mem.singular")}</p>
              {/* The name as the page's own line, with the number beside it:
                  the two together are how a member is referred to out loud. */}
              <div className="mt-0.5 flex flex-wrap items-center gap-2.5">
                <span className="text-heading text-xl leading-7 font-semibold break-words uppercase">
                  {nameWithSuffix(m)}
                </span>
                <span className="bg-surface-inset text-fg-muted text-callout rounded-full px-2.5 py-1 font-medium">
                  {m.account_number}
                </span>
              </div>
            </div>
          </>
        )}
      </nav>

      {/* banner: the membership's own artwork over the card's navy, the logo
          top left and the card's own line bottom right, as legacy drew it.

          Narrow, it is legacy's compact banner (`MrmDetailBanner.scss`, the
          768px mixin): 176px rather than 240, a 28px logo at 12/16, and the
          card's line moved from the bottom right to the top right, kept to 62%
          of the width. The line has to move. Bottom right, it is where the
          avatar comes up through — legacy's compact avatar overlaps the banner
          by 16px at the left, but on a 380px frame "Gold Complimentary
          (Primary)" is three lines tall and reaches back across the card. */}
      <div className="relative">
        <div
          className="rounded-card bg-brand-700 relative h-44 overflow-hidden bg-cover bg-center md:h-60"
          style={bannerArt ? { backgroundImage: `url(${bannerArt})` } : undefined}
        >
          <img
            src={logo}
            alt="S&R"
            className="absolute top-3 left-4 h-7 w-auto md:top-5 md:left-6 md:h-10"
          />
          <div className="absolute top-3 right-4 max-w-[62%] text-right text-white md:top-auto md:right-6 md:bottom-5 md:max-w-[calc(100%-7rem)]">
            {m.card_number ? (
              <p className="text-xs leading-4 font-medium md:text-[1rem] md:leading-6">
                {m.card_number}
              </p>
            ) : null}
            {/* 16/24/32px, which is legacy's three sizes read off its two
                max-width breakpoints from the narrow end up. */}
            {bannerTitle ? (
              <p className="text-[1rem] leading-5 font-bold break-words text-[#feffa3] md:text-[1.5rem] md:leading-8 lg:text-[2rem] lg:leading-10">
                {bannerTitle}
              </p>
            ) : null}
          </div>
        </div>
        {/* Legacy's own geometry, to the pixel (`MrmDetailBanner.scss`): a
            160px avatar 32px in, overlapping the 240px banner by 50px, 24px of
            air after it, and the name centred against the avatar's height
            rather than sitting on its floor — bottom-aligned, two short lines
            beside a tall picture left a band of nothing above them.

            The 50px belongs to the picture, not to the row. On the row it
            lifted the name into the banner too, which is a dark navy panel
            that paints over anything not positioned: a name long enough to
            take two lines had its first one sliced off along the banner's
            bottom edge. Carried by the picture instead, the row still begins
            where the banner ends and still ends where it did — the picture
            reaches up out of it on its own — so no name, at any length, can
            climb into the artwork.

            Under 768px it is legacy's compact block instead, and a column: a
            96px avatar 16px in, overlapping by 16px, then the name and then the
            actions, each on its own full-width line. Sideways there is nothing
            to lay out — 160px of picture and a button group leave a name column
            an inch wide, which is what put one letter on each of eleven lines.
            The overlap stays on the picture for the same reason it does above,
            and the arithmetic still lands where legacy's does: 96 of picture
            less the 16 it has climbed, plus a 12px gap, is the 92px legacy
            gives `margin-top` on its compact `below-row`. */}
        <div className="flex flex-col items-start gap-3 px-4 md:flex-row md:flex-wrap md:items-center md:gap-6 md:px-8">
          <div className="relative -mt-4 shrink-0 md:-mt-[50px]">
            {/* The same picker an employee's photograph uses: a file or the
                camera, framed before it is saved. The ID card stands in when
                there is no photograph, but only a photograph is ever written. */}
            <AvatarPicker
              src={m.profile_image ?? m.id_card_image}
              name={fullName(m) || t("mem.singular")}
              // A frame with nobody in it yet, not two large initials: this is
              // the member's photograph, and legacy drew the empty one as the
              // grey figure.
              placeholder="person"
              /**
               * The white rim is an outline, not a border — legacy's
               * `outline: 4px solid white` sits *outside* the 160px picture, so
               * the circle people see is 168px across. Drawn as a border it ate
               * into the picture instead, leaving a 160px circle: the same badge
               * at the same offset then hung further off the rim than legacy's,
               * which is exactly how it looked.
               */
              // Legacy's own two-layer lift rather than this app's `shadow-md`:
              // the picture sits half off a dark banner onto a white page, and
              // `shadow-md` is too tight a drop to separate it from either.
              className={cn(
                "ring-surface-raised size-24 rounded-full ring-4 md:size-40",
                "shadow-[0_12px_16px_-4px_rgba(16,24,40,0.08),0_4px_6px_-2px_rgba(16,24,40,0.03)]",
              )}
              busy={picture.isPending}
              editable={mayTakePhoto}
              // Legacy's own heading for this sheet, and the one the clerk
              // asked for: it is the member's picture, not "a picture".
              title={t("md.memberImage")}
              onChange={(dataUrl, fileName) =>
                picture.mutate({ profile_image: dataUrl, file_name: fileName })
              }
              onRemove={
                m.profile_image ? () => picture.mutate({ profile_image: "" }) : undefined
              }
            />
            {/* The volume-shopper mark: lit when the member is one, and the
                way to change it for whoever may. */}
            <Tooltip
              label={
                // Say which of the two it is. One message for both read as a
                // permission problem to the one person who has every permission.
                !allow("change__member")
                  ? t("md.noPermission")
                  : frozen
                    ? t("md.frozenAccount")
                    : m.volume_shopper
                      ? t("md.volumeShopperOn")
                      : t("md.volumeShopperOff")
              }
              side="bottom"
              // `mrm-detail-banner__avatar-icon`: 44px at -6px on both edges,
              // so it rides the circle's lower-right rim.
              className="absolute -right-1 -bottom-1 md:-right-1.5 md:-bottom-1.5"
            >
              <button
                type="button"
                disabled={!mayTagVolumeShopper}
                onClick={() => setTagging(true)}
                aria-pressed={m.volume_shopper}
                /**
                 * Legacy's own values rather than this app's tokens: #1a1a1a
                 * lit, #98a2b3 unlit, a 2px ring in white or #d0d5dd to match,
                 * and its soft drop shadow. The mark means the same thing in
                 * both systems and people read it by its colour, so it is the
                 * one badge here that is copied rather than themed. A ring, not
                 * a border — legacy outlines *outside* the 44px, and a border
                 * would eat into the disc instead.
                 */
                className={cn(
                  "grid size-8 place-items-center rounded-full text-white ring-2 md:size-11",
                  "shadow-[0_4px_8px_0_rgba(25,24,27,0.08)] transition-colors",
                  m.volume_shopper
                    ? "bg-[#1a1a1a] ring-white hover:bg-[#2c2c2c]"
                    : "bg-[#98a2b3] ring-[#d0d5dd] hover:bg-[#8794a5]",
                  !mayTagVolumeShopper && "cursor-not-allowed",
                )}
              >
                <ShoppingCart className="size-4 md:size-[22px]" aria-hidden />
                <span className="sr-only">{t("mem.volumeShopper")}</span>
              </button>
            </Tooltip>
          </div>
          <div className="w-full min-w-0 md:flex-1">
            {/* Legacy's 30px where there is room for it, and its two steps down
                where there is not: 24 under 1024, 20 under 768. A member's name
                is theirs — it is not truncated on their own page — so a long one
                wraps, and `break-words` catches the ones with no space to wrap
                at. */}
            <h1 className="text-heading text-xl leading-7 font-semibold break-words uppercase md:text-2xl md:leading-8 lg:text-[1.875rem] lg:leading-[2.375rem]">
              {nameWithSuffix(m)}
            </h1>
            <p className="text-fg-muted text-body mt-1 flex flex-wrap items-center gap-3 font-bold">
              {t("md.accountNumber")} {m.account_number}
              <Badge tone={STATUS_TONE[m.account_status] ?? "neutral"}>{status}</Badge>
              {m.is_temporarily_active ? (
                <Badge tone="info">{t("mem.temporarilyActive")}</Badge>
              ) : null}
            </p>
          </div>
          {/* Its own line on a narrow frame, against the left edge like the two
              above it — legacy's compact `actions` take the full width. */}
          <div className="flex w-full items-center gap-2 md:w-auto">
            {canPrint ? (
              <Tooltip label={printBlock} side="bottom">
                <Button size="sm" disabled={!!printBlock} onClick={() => setPrinting(true)}>
                  <Printer aria-hidden /> {t("md.print")}
                </Button>
              </Tooltip>
            ) : null}
            <IconButton label={t("rp.refresh")} variant="secondary" onClick={refresh}>
              <RefreshCw aria-hidden />
            </IconButton>
          </div>
        </div>
      </div>

      {/* three columns */}
      <div className="mt-8 grid gap-8 lg:grid-cols-[16rem_minmax(0,1fr)_17rem]">
        <dl className="text-callout grid content-start gap-5">
          <Item
            label={t("mem.acquisitionType")}
            onEdit={
              canEditAcquisition && m.membership_tier === "member"
                ? () => setEditingAcquisition(true)
                : undefined
            }
          >
            <Badge tone="brand">{label(ACQUISITION(t), m.acquisition_type)}</Badge>
          </Item>
          {/* Under the channel, not at the foot of the rail: the two are the
              only things here anyone edits, and consent was six read-only rows
              away from the pencil beside it. */}
          <Item
            label={t("md.membersConsent")}
            onEdit={canEditConsent ? () => setEditingConsent(true) : undefined}
          >
            {/* Legacy's order — the two channels, then the narrowing that
                applies to them — and its N/A rather than a dash, which is what
                every other empty row on this page says. */}
            {m.email_consent || m.phone_consent || m.only_important_updates ? (
              <span className="flex flex-wrap gap-1.5">
                {m.email_consent ? <Badge tone="success">{t("md.email")}</Badge> : null}
                {m.phone_consent ? <Badge tone="success">{t("md.mobile")}</Badge> : null}
                {m.only_important_updates ? (
                  <Badge tone="success">{t("md.onlyImportantUpdates")}</Badge>
                ) : null}
              </span>
            ) : (
              t("md.na")
            )}
          </Item>
          <Item label={t("mem.accountType")}>{label(ACCOUNT_TYPE(t), m.account_type)}</Item>
          <Item label={t("mem.memberSince")}>{day(m.created_at)}</Item>
          <Item label={t("mem.signupClub")}>{m.signup_club_name ?? t("md.na")}</Item>
          <Item label={t("mem.renewClub")}>{m.renew_club_name ?? t("md.na")}</Item>
          <Item label={t("mem.activeClub")}>{m.active_club_name ?? t("md.na")}</Item>
          {/* Below the clubs, and on one line rather than label-above-value:
              the answer is a switch, and a switch under its own label reads as
              a setting nobody has set. Only where there is one to be — the free
              card is a Business Primary's to give. */}
          {m.account_type === "supplementary" && m.membership_type === "business" ? (
            <div className="flex items-center gap-2">
              <dt className="text-fg text-body">{t("mem.freeSupplementary")}</dt>
              <dd className="flex items-center gap-2">
                <Checkbox
                  checked={Boolean(m.is_free_supplementary)}
                  disabled
                  readOnly
                  aria-label={t("mem.freeSupplementary")}
                />
                {canSetFree ? <EditIcon onClick={() => setEditingFree(true)} /> : null}
              </dd>
            </div>
          ) : null}
        </dl>

        <div className="grid content-start gap-8">
          <Group
            title={t("md.cardInformation")}
            onEdit={canEditCard ? () => setEditingCard(true) : undefined}
          >
            <Pair k={t("mem.cardName")} v={cardNameOf(m)} />
            <Pair k={t("mem.cardNumber")} v={m.card_number ?? t("md.na")} />
            <Pair k={t("mem.temporaryCardNumber")} v={m.temporary_card_number ?? t("md.na")} />
            <Pair
              k={t("md.validUntil")}
              v={m.no_expiry ? t("mem.noExpiry") : day(m.valid_until)}
            />
            <Pair k={t("md.cardStatus")} v={status} />
          </Group>
          <Group
            title={t("md.basicInformation")}
            onEdit={canEditBasic ? () => setCard("basic") : undefined}
            shown={showBasic}
            onToggle={() => setShowBasic((s) => !s)}
          >
            <Pair k={t("mem.prefix")} v={masked(label(PREFIX(t), m.prefix))} />
            <Pair k={t("mem.suffix")} v={masked(label(SUFFIX(), m.suffix))} />
            <Pair k={t("mem.fullName")} v={masked(fullName(m))} />
            <Pair k={t("mem.mobileNumber")} v={masked(m.mobile_number)} />
            <Pair k={t("mem.emailAddress")} v={masked(m.email)} />
            <Pair k={t("mem.dateOfBirth")} v={showBasic ? day(m.date_of_birth) : MASK} />
            <Pair k={t("mem.gender")} v={masked(label(GENDER(t), m.gender))} />
            <Pair k={t("mem.unitNumber")} v={masked(m.unit_number)} />
            <Pair k={t("mem.buildingName")} v={masked(m.building_name)} />
            <Pair k={t("mem.addressStreet")} v={masked(m.address_street)} />
            <Pair k={t("mem.city")} v={masked(m.city_name)} />
            <Pair k={t("mem.barangay")} v={masked(m.barangay_name)} />
            {m.membership_tier === "employee" ? (
              <Pair k={t("mem.employeeNumber")} v={masked(m.employee_number)} />
            ) : null}
            {m.membership_tier === "affiliates" ? (
              <Pair k={t("mem.companyName")} v={masked(m.company_name)} />
            ) : null}
          </Group>
          {/* A volume shopper's, and nobody else's: the business is why the
              member buys by the case, so legacy showed this section only when
              the cart is lit (`columns.middleBusiness.length > 0 &&
              isVolumeShopper`). Behind a link even then — most have nothing on
              file, and four empty rows say less than one line. */}
          {m.volume_shopper ? (
            <section>
              <button
                type="button"
                onClick={() => setShowBusiness((s) => !s)}
                className="text-accent text-body font-medium hover:underline"
              >
                {showBusiness ? t("md.viewLess") : t("md.viewMore")}
              </button>
              {showBusiness ? (
                <div className="mt-4">
                  <Group
                    title={t("md.businessInformation")}
                    onEdit={canChange ? () => setCard("business") : undefined}
                    shown={showBusinessInfo}
                    onToggle={() => setShowBusinessInfo((s) => !s)}
                  >
                    <Pair k={t("mem.businessName")} v={businessMasked(m.business_name)} />
                    <Pair
                      k={t("mem.businessCompanyName")}
                      v={businessMasked(m.business_company_name)}
                    />
                    <Pair k={t("mem.businessType")} v={businessMasked(m.business_type)} />
                    <Pair k={t("mem.position")} v={businessMasked(m.position)} />
                  </Group>
                </div>
              ) : null}
            </section>
          ) : null}
        </div>

        <div className="text-callout grid content-start gap-6">
          {/* Beside the member rather than in the Details tabs: an attachment
              is something a person puts on a record, not a history of it.
              Add was asked for and View was not, so View opened a list the API
              then refused — and with neither, the row sat there labelled and
              empty, which reads as a member with no documents rather than as
              documents that are not yours to see. */}
          {allow("view__member_attachment", "add__member_attachment") ? (
            <Item label={t("md.attachments")}>
              <div className="flex flex-wrap gap-2">
                {allow("view__member_attachment") ? (
                  <Button variant="outline" onClick={() => setViewing("attachments")}>
                    {t("rp.view")}
                  </Button>
                ) : null}
                {allow("add__member_attachment") ? (
                  <Button onClick={() => setAddingAttachment(true)}>
                    {t("rp.addSingular").replace("{singular}", t("hist.attachment"))}
                  </Button>
                ) : null}
              </div>
            </Item>
          ) : null}
          {/* Not drawn at all where there is nothing to take — the matrix's
              "Not Visible", and its "N/A": a blacklisted account, an employee
              supplementary that cannot exist, an expired employee account in
              front of an Associate. A control offering nothing is a control
              that reads as broken. Still drawn while the answer is loading, so
              the row does not appear a moment after the rest of the card. */}
          {allowed.isPending || (allowed.data ?? []).length > 0 ? (
            <Item label={t("mem.amendment")}>
              {/* Only what the server says this account can take right now, for
                  this person: the same table the API checks, so a control that
                  is not here is not one that can be reached by asking. */}
              <OptionSelect
                value=""
                onChange={(next) => setAmendment((next || null) as Amendment | null)}
                disabled={!canAmend || allowed.isPending}
                // Nothing is ever chosen here — picking one opens its dialog and
                // the control goes back to empty — so there is nothing to clear.
                clearable={false}
                placeholder={t("md.selectAmendment")}
                choices={(allowed.data ?? []).map((a) => ({
                  value: a,
                  label: t(`md.amendName.${a}` as TranslationKey),
                }))}
              />
            </Item>
          ) : null}
          {m.account_type === "supplementary" ? (
            <Item label={t("md.primaryAccountNo")}>
              <Input readOnly value={m.parent_account_number ?? ""} placeholder="—" />
              {/* Moving a supplementary to another primary posts to
                  `/amendments/change-primary-account`, so it is an amendment
                  and asks what the dropdown above it asks. */}
              {canAmend ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() => setChangingAccount(true)}
                >
                  {t("md.changeAccountNo")}
                </Button>
              ) : null}
            </Item>
          ) : TAKES_SUPPLEMENTARY.has(m.membership_tier ?? "") ? (
            <Item label={t("md.supplementaryAccount")}>
              {/* The numbers themselves, read only: the primary's side of the
                  link is a fact about the account, and "a supplementary card
                  hangs off this account" said nothing a clerk could act on —
                  they still had to go and find which one. Moving one is the
                  supplementary's own amendment, which is why there is nothing
                  to press here. */}
              {(m.supplementary_account_numbers ?? []).length > 0 ? (
                <div className="grid gap-2">
                  {(m.supplementary_account_numbers ?? []).map((number) => (
                    <Input key={number} readOnly value={number} />
                  ))}
                </div>
              ) : (
                <p className="text-fg-muted">{t("md.noSupplementary")}</p>
              )}
              {/* Straight to the parent gate with this account filled in, so
                  the clerk is not asked to retype the number on the screen.
                  Gone once the slots configured for this membership are taken:
                  the gate refuses it anyway, and a button that only leads to a
                  refusal is worse than no button. */}
              {canAddSupplementary && m.account_status === "active" && !slotsFull ? (
                <Button asChild variant="secondary" size="sm" className="mt-2">
                  <Link
                    // This member's own tier, not a hardcoded `member`. A
                    // supplementary takes its membership from the primary it
                    // hangs off, so the tier travelling with the number is what
                    // lets the next screen refuse an account of another one —
                    // it used to say `member` from every page, which is how a
                    // complimentary account passed a gate opened from a Gold
                    // member and the other way round.
                    to={`/members/new?tier=${encodeURIComponent(
                      m.membership_tier ?? "member",
                    )}&parent=${encodeURIComponent(m.account_number)}`}
                  >
                    {t("md.addSupplementary")}
                  </Link>
                </Button>
              ) : null}
            </Item>
          ) : null}
          <Group
            title={t("md.coBrandTagging")}
            shown={showCoBrand}
            onToggle={() => setShowCoBrand((s) => !s)}
          >
            <Pair k={t("md.coBrand")} v={showCoBrand ? (m.co_brand_tagging ?? "—") : MASK} />
          </Group>
          {allow("view__member_note", "add__member_note") ? (
            <Item label={t("hist.notes")}>
              <div className="flex flex-wrap gap-2">
                {allow("view__member_note") ? (
                  <Button variant="outline" onClick={() => setViewing("notes")}>
                    {t("rp.view")}
                  </Button>
                ) : null}
                {allow("add__member_note") ? (
                  <Button onClick={() => setNoteForm({ note: null })}>
                    {t("rp.addSingular").replace("{singular}", t("hist.note"))}
                  </Button>
                ) : null}
              </div>
            </Item>
          ) : null}
        </div>
      </div>

      <MemberHistories
        memberId={id}
        active={tab}
        onActive={setTab}
        tier={m.membership_tier}
        accountType={m.account_type}
      />

      {viewing === "attachments" ? (
        <ViewAttachmentsModal
          memberId={id}
          accountNumber={m.account_number}
          onClose={() => setViewing(null)}
        />
      ) : null}
      {addingAttachment ? (
        <AddAttachmentModal memberId={id} onClose={() => setAddingAttachment(false)} />
      ) : null}
      {viewing === "notes" ? (
        <ViewNotesModal
          memberId={id}
          accountNumber={m.account_number}
          onClose={() => setViewing(null)}
          onEdit={(note) => setNoteForm({ note })}
        />
      ) : null}
      {noteForm ? (
        <NoteFormModal
          memberId={id}
          note={noteForm.note}
          approvedById={m.approved_by_id}
          onClose={() => setNoteForm(null)}
        />
      ) : null}

      <AmendmentDialog
        key={amendment ?? "none"}
        memberId={id}
        action={amendment}
        currentTier={m.membership_tier}
        currentAcquisition={m.acquisition_type}
        currentAccountNumber={m.account_number}
        currentParentAccountNumber={m.parent_account_number}
        onClose={() => setAmendment(null)}
        onDone={() => {
          setAmendment(null);
          refresh();
        }}
      />
      <PrintDialog
        member={m}
        open={printing}
        onClose={() => setPrinting(false)}
        onPrinted={refresh}
      />

      <Modal
        open={tagging}
        onClose={() => setTagging(false)}
        size="sm"
        // Legacy's own `width="480px"`, which falls between this app's sm and
        // md sheets: the two radios and the sentence above them read as one
        // block at that width and wrap at anything narrower.
        className="max-w-[480px]"
        // No rule over the actions: the body is a sentence and two radios, and
        // the hairline is there to say where a long scrolling body ends.
        // Legacy's footer draws none either.
        footerDivider={false}
        title={t("mem.volumeShopper")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTagging(false)}>
              {t("common.cancel")}
            </Button>
            {/* Submit, not Save: legacy's `BaseModal` labels its confirm
                button Submit, and this sheet applies a flag rather than
                saving an edited record. */}
            <Button type="submit" form="volume-shopper" loading={volume.isPending}>
              {t("common.submit")}
            </Button>
          </>
        }
      >
        {/* Mounted with the dialog, so the radios always start from what is on
            record: kept in the tree, the pair held whatever was last picked —
            Cancel, reopen, and the choice was still sitting there, which reads
            as a change that saved itself. Legacy synced the same state back to
            the member's value every time it changed. */}
        {tagging ? (
          <VolumeShopperForm value={m.volume_shopper} onSubmit={(on) => volume.mutate(on)} />
        ) : null}
      </Modal>

      <Modal
        open={editingFree}
        onClose={() => setEditingFree(false)}
        size="sm"
        title={t("mem.freeSupplementary")}
        description={t("md.freeSupplementaryBody")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditingFree(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={freeCard.isPending}
              onClick={() => freeCard.mutate(!m.is_free_supplementary)}
            >
              {m.is_free_supplementary
                ? t("md.freeSupplementaryOff")
                : t("md.freeSupplementaryOn")}
            </Button>
          </>
        }
      >
        <p className="text-fg text-body flex items-center gap-3 py-2">
          <Checkbox
            checked={Boolean(m.is_free_supplementary)}
            disabled
            readOnly
            aria-label={t("mem.freeSupplementary")}
          />
          {m.is_free_supplementary
            ? t("md.freeSupplementaryIsOn")
            : t("md.freeSupplementaryIsOff")}
        </p>
      </Modal>

      {/* Two sheets of its own: the number is validated and the account it
          names shown, then confirmed, before the card is reissued under it. */}
      {changingAccount ? (
        <PrimaryMoveDialog
          action="change-primary-account"
          memberId={m.id}
          accountNumber={m.account_number}
          parentAccountNumber={m.parent_account_number}
          currentAcquisition={m.acquisition_type}
          onClose={() => setChangingAccount(false)}
          onDone={refresh}
        />
      ) : null}

      {editingAcquisition ? (
        <AcquisitionDialog
          member={m}
          onClose={() => setEditingAcquisition(false)}
          onSaved={() => {
            setEditingAcquisition(false);
            refresh();
          }}
        />
      ) : null}

      {editingCard ? (
        <CardInfoDialog
          member={m}
          onClose={() => setEditingCard(false)}
          onSaved={() => {
            setEditingCard(false);
            refresh();
          }}
        />
      ) : null}

      {editingConsent ? (
        <ConsentDialog
          member={m}
          onClose={() => setEditingConsent(false)}
          onSaved={() => {
            setEditingConsent(false);
            refresh();
          }}
        />
      ) : null}

      {card ? (
        <EditDialog
          singular={t(CARDS[card].title)}
          endpoint="/members"
          action={CARDS[card].action}
          fields={cardFields(card)}
          initial={toForm(m)}
          id={m.id}
          stamp={m.updated_at}
          columns={card === "basic" ? 2 : 1}
          onClose={() => setCard(null)}
          onSaved={() => {
            setCard(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/** Legacy's two radios: the flag is a decision, and a checkbox reads as a detail. */
function VolumeShopperForm({
  value,
  onSubmit,
}: {
  value: boolean;
  onSubmit: (on: boolean) => void;
}) {
  const t = useTranslation();
  const [on, setOn] = useState(value);
  return (
    <Form
      id="volume-shopper"
      className="py-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(on);
      }}
    >
      {/* What the flag does, in the body rather than under the title. Legacy
          put it there (`mrm-volume-shopper-modal__description`) and the room
          that buys it is the point: a sentence this long sitting tight under
          the heading reads as part of the heading. */}
      <p className="text-fg-muted text-subhead">{t("md.volumeShopperBody")}</p>
      {/* And this says what the two rows under it are for
          (`__section-label`: 16px above, 8px below). Without it the radios
          hang off the end of the sentence as though they were part of it. */}
      <p className="text-fg text-subhead mt-8 mb-2 font-medium">
        {t("md.volumeShopperStatus")}
      </p>
      <div className="grid gap-2">
        {(
          [
            [true, t("md.enableVolumeShopper")],
            [false, t("md.disableVolumeShopper")],
          ] as const
        ).map(([choice, text]) => (
          <label key={String(choice)} className="text-fg text-body flex items-center gap-3">
            <input
              type="radio"
              name="volume_shopper"
              className="accent-accent size-4"
              checked={on === choice}
              onChange={() => setOn(choice)}
            />
            {text}
          </label>
        ))}
      </div>
    </Form>
  );
}

function Item({
  label: text,
  children,
  onEdit,
}: {
  label: string;
  children: ReactNode;
  onEdit?: (() => void) | undefined;
}) {
  return (
    <div>
      <dt className="text-fg-muted text-body flex items-center gap-2">
        {text}
        {onEdit ? <EditIcon onClick={onEdit} /> : null}
      </dt>
      <dd className="text-fg text-body mt-1.5">{children}</dd>
    </div>
  );
}

function Group({
  title,
  children,
  onEdit,
  shown,
  onToggle,
}: {
  title: string;
  children: ReactNode;
  onEdit?: (() => void) | undefined;
  shown?: boolean;
  onToggle?: () => void;
}) {
  const t = useTranslation();
  return (
    <section>
      <h3 className="text-fg text-body flex items-center gap-2 font-bold">
        {title}
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            className="text-fg-subtle hover:text-fg grid size-6 place-items-center"
          >
            {shown ? (
              <EyeOff className="size-4" aria-hidden />
            ) : (
              <Eye className="size-4" aria-hidden />
            )}
            <span className="sr-only">{shown ? t("md.hide") : t("md.show")}</span>
          </button>
        ) : null}
        {onEdit ? <EditIcon onClick={onEdit} /> : null}
      </h3>
      {/* Label and value on one line at every width, which is legacy's
          `&__info-block .detail-row`: a row, 16px apart, over a label column
          fixed at 120px so every value on the card starts at the same place.
          Stacked — which is what this did under `sm` — a fifteen-row card is
          thirty lines of alternating grey and black, and reading down it for
          one value means counting pairs. The value keeps `minmax(0,1fr)`, so a
          long one wraps inside its own column rather than widening the grid. */}
      <dl className="text-body mt-3 grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-4 gap-y-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
        {children}
      </dl>
    </section>
  );
}

function Pair({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt className="text-fg-muted">{k}</dt>
      <dd className="text-fg break-words">{v}</dd>
    </>
  );
}

function EditIcon({ onClick }: { onClick: () => void }) {
  const t = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-fg-subtle hover:text-fg grid size-6 place-items-center"
    >
      <Pencil className="size-4" aria-hidden />
      <span className="sr-only">{t("common.edit")}</span>
    </button>
  );
}

export default MemberDetailPage;
